import type { ReactNode } from 'react'

// Shared "info chip" shape — was hand-duplicated across AccountPage,
// UsersPage, MetersPage, RoutesPage, ScadaView, TasksPage (docs/plan-
// shared-design-tokens.md). `tone` covers the color pairs that already
// repeat as-is in 2+ places; anything more one-off (a caller-supplied
// color list, a state-dependent ternary) stays on `className` so this
// component doesn't have to guess a semantic mapping for colors it
// wasn't asked to judge.
export type BadgeTone = 'info' | 'neutral' | 'success' | 'warning' | 'teal' | 'slate' | 'critical' | 'problem'

const TONE_CLASSES: Record<BadgeTone, string> = {
  info:     'bg-blue-50 text-brand-cyan-text border border-blue-200',
  neutral:  'bg-gray-100 text-gray-700',
  success:  'bg-emerald-100 text-emerald-800',
  warning:  'bg-amber-100 text-amber-800',
  teal:     'bg-teal-100 text-teal-800',
  slate:    'bg-slate-100 text-slate-700',
  critical: 'bg-red-50 text-brand-critical-text border border-red-200',
  // Problem Orange (branding guide §3b) -- warning-severity signals, distinct
  // from the amber 'warning' tone above, which predates the severity scale.
  problem:  'bg-orange-50 text-brand-warning-text border border-orange-200',
}

// Backend status/alert severity -> chip tone, one mapping for every surface.
export function severityTone(sev: 'critical' | 'warning' | 'unknown' | 'ok' | null | undefined): BadgeTone {
  if (sev === 'critical') return 'critical'
  if (sev === 'warning')  return 'problem'
  if (sev === 'ok')       return 'info'
  return 'slate'
}

export function Badge({
  tone, className = '', dot = false, dotClassName = '', title, children,
}: {
  tone?: BadgeTone
  className?: string
  dot?: boolean
  dotClassName?: string
  title?: string
  children: ReactNode
}) {
  return (
    <span title={title} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${tone ? TONE_CLASSES[tone] : ''} ${className}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotClassName}`} />}
      {children}
    </span>
  )
}
